import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import { SearchBar as SearchBarElement } from 'dibs-elements/exports/SearchBar';

import { SearchBarOverlay } from './SearchBarOverlay';
import {
    dedupeLocalStorage,
    getCursorTerms,
    handleRecentStorage,
    cleanUpRecentSearches,
} from '../../utils/sharedSearchBarHelpers';
import {
    KEY_DOWN_VALUE,
    KEY_UP_VALUE,
    SEARCH_PAGE_PATH,
    SEARCH_QUERY_PARAM,
    TRACKING,
    ORIGINAL_SEARCH_QUERY_PARAM,
} from '../../utils/sharedSearchBarConstants';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

import styles from './SearchBar-style.scss';

const MIN_NUM_VALUE_CHARACTERS = 2;

const messages = defineMessages({
    placeholder: {
        id: 'dbl.Header.SearchBar.placeholder',
        defaultMessage: 'Search for items, creators, or styles',
    },
});

export const SearchBarComponent = props => {
    const intl = useIntl();
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isSearchBarCleared, setIsSearchBarCleared] = useState(false);
    const [cursorTerms, setCursorTerms] = useState([]);
    const [cursorPosition, setCursorPosition] = useState(-1);
    const [cursorHighlightId, setCursorHighlightId] = useState(undefined);
    const overlayWrapper = useRef(null);

    const { hasRootPendingRefetch, isClient, relay, viewer } = props;
    const { searchSuggestions, user } = viewer;
    const { suggestions } = searchSuggestions || {};
    const isSuggestionsFeatureEnabled = get(viewer, 'isSuggestionsFeatureEnabled');
    const recentSearches = dedupeLocalStorage() || [];
    const dropdownChoices = suggestions || recentSearches;
    const isVisible = isFocused || isHovered;

    const onFocus = useCallback(() => {
        setIsFocused(true);
    }, [setIsFocused]);

    const onBlur = useCallback(() => {
        setIsFocused(false);
    }, [setIsFocused]);

    const onMouseOver = useCallback(() => {
        setIsHovered(true);
    }, [setIsHovered]);

    const onMouseOut = useCallback(() => {
        setIsHovered(false);
    }, [setIsHovered]);

    const setSearchTermFromParams = useCallback(() => {
        const { hash, href, search } = location;
        const hashParams = new URLSearchParams(get(hash.split('?'), '[1]'));
        const oqParam = hashParams.get(ORIGINAL_SEARCH_QUERY_PARAM);

        if (href.indexOf(SEARCH_PAGE_PATH) !== -1 || oqParam) {
            const searchParams = new URLSearchParams(search);
            const searchTerm = oqParam || searchParams.get(SEARCH_QUERY_PARAM);

            if (searchTerm) {
                setValue(searchTerm);
            }
        }
    }, [setValue]);

    const handleRowClick = useCallback(
        (val, href) => {
            const label = searchSuggestions
                ? 'global search|auto-complete selected'
                : 'recent search';
            setValue(val);
            setIsFocused(false);
            setIsHovered(false);

            trackEvent({
                category: TRACKING.CATEGORY,
                action: TRACKING.ACTION,
                label,
            });

            handleRecentStorage(val, href);
            location.assign(href);
        },
        [searchSuggestions, setValue, setIsFocused, setIsHovered]
    );

    const handleCursorChange = useCallback(
        move => {
            const newCursorTerms = cursorTerms.length
                ? cursorTerms
                : getCursorTerms(dropdownChoices, value, intl);
            let newCursorPosition = cursorPosition;

            if (!dropdownChoices) {
                // if we have no section values exit out
                return;
            }

            switch (move) {
                case KEY_UP_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition - 1 + newCursorTerms.length) % newCursorTerms.length
                            : newCursorTerms.length - 2;
                    break;
                case KEY_DOWN_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition + 1) % newCursorTerms.length
                            : 0;
                    break;
                default:
                    break;
            }

            const newValue = newCursorTerms[newCursorPosition].term;
            const newHighlightId = newCursorTerms[newCursorPosition].highlightId;
            setValue(newValue);
            setCursorHighlightId(newHighlightId);
            setCursorTerms(newCursorTerms);
            setCursorPosition(newCursorPosition);
        },
        [
            dropdownChoices,
            value,
            intl,
            cursorPosition,
            cursorTerms,
            setCursorPosition,
            setCursorTerms,
            setCursorHighlightId,
        ]
    );

    const handleKeyDown = event => {
        if (event.keyCode === 27) {
            onBlur();
        }

        if (event.keyCode === 40) {
            event.preventDefault();
            handleCursorChange(KEY_DOWN_VALUE);
        }

        if (event.keyCode === 38) {
            event.preventDefault();
            handleCursorChange(KEY_UP_VALUE);
        }
    };

    const fetchSuggestions = useCallback(() => {
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });
        const fragmentVariables = {
            hasQuery: value.length > 1,
            query: value.toLowerCase(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
        };

        if (!hasRootPendingRefetch) {
            relay.refetch(
                () => fragmentVariables,
                { ...fragmentVariables, fetchUserRecentHistoryItem: false },
                () => setIsSearchBarCleared(false)
            );
        }
    }, [value, relay, hasRootPendingRefetch]);

    const [debouncedFetchSuggestions] = useDebouncedCallback(fetchSuggestions, 250);

    const handleChange = useCallback(
        newValue => {
            if (!newValue) {
                setIsSearchBarCleared(true);
            }
            setValue(newValue);
            setCursorTerms([]);
            setCursorHighlightId(null);
            setCursorPosition(-1);
        },
        [setValue, setCursorTerms, setCursorPosition]
    );

    const handleSubmit = useCallback(
        event => {
            event.preventDefault();
            let urlLabel;

            if (cursorPosition > -1 && cursorTerms[cursorPosition].link) {
                handleRowClick(cursorTerms[cursorPosition].term, cursorTerms[cursorPosition].link);
            } else if (value) {
                urlLabel = `/search/?q=${encodeURIComponent(value.toLowerCase())}`;
                trackEvent({
                    category: TRACKING.CATEGORY,
                    action: TRACKING.ACTION,
                    label: 'global search',
                });
                handleRecentStorage(value, urlLabel);
                location.assign(`${location.origin}${urlLabel}`);
            }
        },
        [value, cursorTerms, cursorPosition, handleRowClick]
    );

    useEffect(() => {
        cleanUpRecentSearches();
    }, []);

    useEffect(() => {
        if (value && isSuggestionsFeatureEnabled && !cursorHighlightId) {
            debouncedFetchSuggestions(value);
        }
    }, [cursorHighlightId, value, debouncedFetchSuggestions, isSuggestionsFeatureEnabled]);

    useEffect(() => {
        if (overlayWrapper && overlayWrapper.current) {
            overlayWrapper.current.addEventListener('mouseover', onMouseOver);
            overlayWrapper.current.addEventListener('mouseout', onMouseOut);
            setSearchTermFromParams();
        }
    }, [overlayWrapper, setSearchTermFromParams, onMouseOut, onMouseOver]);

    const showRecentlyViewed =
        !suggestions || value.length < MIN_NUM_VALUE_CHARACTERS || isSearchBarCleared;

    return (
        <div className={styles.wrapper}>
            <form className={styles.form} action="/search/" role="search" onSubmit={handleSubmit}>
                <div className={styles.searchBar}>
                    <SearchBarElement
                        dataTn="global-header-search-input"
                        hasClickableSearchButton
                        isClearButtonHidden={!value}
                        noBorder
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage(messages.placeholder)}
                        size="small"
                        value={value}
                    />
                </div>
            </form>
            <div className={styles.overlayWrapper} ref={overlayWrapper}>
                {isClient ? (
                    <SearchBarOverlay
                        overlayClassName={styles.overlay}
                        canShowRecentlyViewed={isVisible && showRecentlyViewed}
                        handleRowClick={handleRowClick}
                        visible={isVisible}
                        suggestions={showRecentlyViewed ? recentSearches : suggestions}
                        searchTerm={value}
                        cursorHighlightId={cursorHighlightId}
                        user={user || null}
                    />
                ) : null}
            </div>
        </div>
    );
};

SearchBarComponent.propTypes = {
    hasRootPendingRefetch: PropTypes.bool,
    isClient: PropTypes.bool,
    relay: PropTypes.object,
    viewer: PropTypes.object,
};

export const SearchBar = createRefetchContainer(
    SearchBarComponent,
    {
        viewer: graphql`
            fragment SearchBar_viewer on Viewer
            @argumentDefinitions(
                query: { type: "String!", defaultValue: "" }
                hasQuery: { type: "Boolean!", defaultValue: false }
            ) {
                isSuggestionsFeatureEnabled: isEnabledByLocale(
                    featureName: "SEARCH_BAR_SUGGESTIONS"
                )
                searchSuggestions(query: $query, includeTypes: ["POPULAR", "CATEGORY", "SELLER"])
                    @include(if: $hasQuery) {
                    suggestions {
                        type
                        terms {
                            term
                            urlLabel
                        }
                    }
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...getRecentHistory_user @defer
                }
            }
        `,
    },
    graphql`
        query SearchBarRefetchQuery(
            $query: String!
            $hasQuery: Boolean!
            $userId: String!
            $hasUserId: Boolean!
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
        ) {
            viewer {
                ...SearchBar_viewer @arguments(query: $query, hasQuery: $hasQuery)
            }
        }
    `
);
